import {
    Box,
    Divider,
    Grid,
    InputLabel,
    List,
    ListItem,
    Stack,
    Typography,
} from '@mui/material';
import React from 'react';
import AddressAutocomplete from '../../../../shared/AddressAutocomplete';
import FormTextField from '../../../../shared/FormTextField';
import { useFormContext, useWatch } from 'react-hook-form';
import { VehicleLocationSchema } from './schema';
import { z } from 'zod';
import { Address } from '../../../../types/experience';
import FormCheckbox from '../../../../shared/FormCheckbox';
import FormAutocomplete from '../../../../shared/FormAutocomplete';
import CustomTooltip from '../../../../shared/CustomTooltip';
import { HelpOutline } from '@mui/icons-material';
import {
    useGetNirovisionLabelsQuery,
    useGetNirovisionLocationsQuery,
} from '../../../../../app/Slices/SevenDayApiSlice';
import { useFormState } from '../useFormState';
import { ExperienceType } from '../../../../enums/experience';

type FormValues = z.infer<typeof VehicleLocationSchema>;

const DropoffDetails = ({
    nirovisionLocationOptions,
    nirovisionLabelOptions,
    nirovisionVehicleLabelOptions,
}: {
    nirovisionLocationOptions: any[];
    nirovisionLabelOptions: any[];
    nirovisionVehicleLabelOptions: any[];
}) => {
    const { control, clearErrors, getValues, setValue } =
        useFormContext<FormValues>();

    const sameAsPickup = useWatch({
        control,
        name: 'details.dropoffLocationSameAsPickup',
    });
    const usesNirovision = useWatch({
        control,
        name: 'details.dropoffUsesNirovision',
    });

    const handleDropOffAddress = (address: Address) => {
        clearErrors([
            'details.dropoffStreetAddress',
            'details.dropoffSuburb',
            'details.dropoffState',
            'details.dropoffPostcode',
            'details.dropoffCountryCode',
        ]);
        setValue('details.dropoffStreetAddress', address.streetAddress, {
            shouldDirty: true,
        });
        setValue('details.dropoffSuburb', address.suburb, {
            shouldDirty: true,
        });
        setValue('details.dropoffState', address.state as FormValues['state'], {
            shouldDirty: true,
        });
        setValue('details.dropoffPostcode', address.postcode, {
            shouldDirty: true,
        });
        setValue(
            'details.dropoffCountryCode',
            address.country as FormValues['countryCode'],
            {
                shouldDirty: true,
            },
        );
    };

    const handleDropoffSameAsPickup = () => {
        if (!sameAsPickup) {
            setValue(
                'details.dropoffStreetAddress',
                getValues('streetAddress'),
            );
            setValue('details.dropoffSuburb', getValues('suburb'));
            setValue('details.dropoffState', getValues('state'));
            setValue('details.dropoffPostcode', getValues('postcode'));
            setValue('details.dropoffCountryCode', getValues('countryCode'));
            setValue('details.dropoffPhone', getValues('phone'));
            setValue(
                'details.dropoffUsesNirovision',
                getValues('usesNirovision'),
            );
            setValue(
                'details.dropoffNirovisionLocationId',
                getValues('nirovisionLocationId'),
            );
            setValue(
                'details.dropoffNirovisionLabelId',
                getValues('nirovisionLabelId'),
            );
            setValue(
                'details.dropoffNirovisionVehicleLabelId',
                getValues('nirovisionVehicleLabelId'),
            );
            return;
        }

        setValue('details.dropoffStreetAddress', '');
        setValue('details.dropoffSuburb', '');
        // @ts-expect-error - TODO: fix this
        setValue('details.dropoffState', '');
        setValue('details.dropoffPostcode', '');
        setValue('details.dropoffCountryCode', 'AU');
        setValue('details.dropoffPhone', '');
        setValue('details.dropoffUsesNirovision', false);
        setValue('details.dropoffNirovisionLocationId', '');
        setValue('details.dropoffNirovisionLabelId', '');
        setValue('details.dropoffNirovisionVehicleLabelId', '');
    };

    return (
        <div>
            <Box display='flex' alignItems='center' gap={1}>
                <FormCheckbox
                    id='dropoff-same-as-pickup__input'
                    name='details.dropoffLocationSameAsPickup'
                    control={control}
                    onClick={handleDropoffSameAsPickup}
                />
                <InputLabel htmlFor='dropoff-same-as-pickup__input'>
                    Dropoff uses same address
                </InputLabel>
            </Box>
            {!sameAsPickup && (
                <>
                    <Typography variant='h3' sx={{ my: 2 }}>
                        Drop off Location
                    </Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <InputLabel>Address</InputLabel>
                            <AddressAutocomplete
                                onChange={handleDropOffAddress}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <InputLabel htmlFor='dropoff-street-address__input'>
                                Street Address
                            </InputLabel>
                            <FormTextField
                                id='dropoff-street-address__input'
                                name='details.dropoffStreetAddress'
                                control={control}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <InputLabel htmlFor='dropoff-suburb__input'>
                                Suburb
                            </InputLabel>
                            <FormTextField
                                id='dropoff-suburb__input'
                                name='details.dropoffSuburb'
                                control={control}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <InputLabel htmlFor='dropoff-state__input'>
                                State
                            </InputLabel>
                            <FormAutocomplete
                                id='dropoff-state__input'
                                name='details.dropoffState'
                                control={control}
                                options={[
                                    { value: 'NSW', label: 'NSW' },
                                    { value: 'QLD', label: 'QLD' },
                                    { value: 'VIC', label: 'VIC' },
                                    { value: 'SA', label: 'SA' },
                                    { value: 'WA', label: 'WA' },
                                    { value: 'NT', label: 'NT' },
                                    { value: 'TAS', label: 'TAS' },
                                    { value: 'ACT', label: 'ACT' },
                                ]}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <InputLabel htmlFor='dropoff-postcode__input'>
                                Postcode
                            </InputLabel>
                            <FormTextField
                                id='dropoff-postcode__input'
                                name='details.dropoffPostcode'
                                control={control}
                                type='number'
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <InputLabel htmlFor='dropoff-country-code__input'>
                                Country
                            </InputLabel>
                            <FormAutocomplete
                                id='dropoff-country-code__input'
                                name='details.dropoffCountryCode'
                                control={control}
                                options={[
                                    {
                                        value: 'AU',
                                        label: 'Australia',
                                    },
                                ]}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <InputLabel htmlFor='dropoff-phone__input'>
                                Property Contact No.
                            </InputLabel>
                            <FormTextField
                                id='dropoff-phone__input'
                                name='details.dropoffPhone'
                                control={control}
                                type='tel'
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <InputLabel htmlFor='dropoff-uses-nirovision__input'>
                                Requires Nirovision Access
                            </InputLabel>
                            <FormAutocomplete
                                id='dropoff-uses-nirovision__input'
                                name='details.dropoffUsesNirovision'
                                control={control}
                                options={[
                                    { value: true, label: 'Yes' },
                                    { value: false, label: 'No' },
                                ]}
                            />
                        </Grid>
                        {usesNirovision && (
                            <>
                                <Grid item xs={12} md={6}>
                                    <InputLabel htmlFor='dropoff-nirovision-location__input'>
                                        Nirovision Location
                                    </InputLabel>
                                    {nirovisionLocationOptions.length > 0 ? (
                                        <FormAutocomplete
                                            id='dropoff-nirovision-location__input'
                                            name='details.dropoffNirovisionLocationId'
                                            control={control}
                                            options={nirovisionLocationOptions}
                                        />
                                    ) : (
                                        <Typography color='error'>
                                            Please link your Nirovision account
                                            to 7DAY in the Admin Centre.
                                        </Typography>
                                    )}
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <InputLabel htmlFor='dropoff-nirovision-label__input'>
                                        Nirovision Label
                                    </InputLabel>
                                    {nirovisionLabelOptions.length > 0 ? (
                                        <FormAutocomplete
                                            id='dropoff-nirovision-label__input'
                                            name='details.dropoffNirovisionLabelId'
                                            control={control}
                                            options={nirovisionLabelOptions}
                                        />
                                    ) : (
                                        <Typography color='error'>
                                            Please link your Nirovision account
                                            to 7DAY in the Admin Centre.
                                        </Typography>
                                    )}
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <InputLabel htmlFor='dropoff-nirovision-vehicle-label__input'>
                                        Nirovision Vehicle Label
                                    </InputLabel>
                                    <FormAutocomplete
                                        id='dropoff-nirovision-vehicle-label__input'
                                        name='details.dropoffNirovisionVehicleLabelId'
                                        control={control}
                                        options={nirovisionVehicleLabelOptions}
                                        onChange={(_, value) => {
                                            setValue(
                                                'details.dropoffNirovisionVehicleLabelId',
                                                value?.value ?? '',
                                                {
                                                    shouldDirty: true,
                                                },
                                            );
                                        }}
                                    />
                                </Grid>
                            </>
                        )}
                    </Grid>
                </>
            )}
        </div>
    );
};

const ExperienceLocationForm = () => {
    const { experience } = useFormState();
    const { control, setValue } = useFormContext<FormValues>();

    const { data: nirovisionLocations = [] } = useGetNirovisionLocationsQuery(
        {},
    );
    const { data: nirovisionLabels = [] } = useGetNirovisionLabelsQuery({});

    const usesNirovision = useWatch({
        control,
        name: 'usesNirovision',
    });

    const nirovisionLocationOptions = nirovisionLocations.map(
        (location: any) => ({
            value: location.id,
            label: `${location.name} (ID: ${location.id})`,
        }),
    );

    const nirovisionLabelOptions = nirovisionLabels
        .filter((label: any) => label.type === 'PERSON')
        .map((label: any) => ({
            value: label.id,
            label: label.name,
        }));

    const nirovisionVehicleLabelOptions = [
        { value: '', label: 'None' },
        ...nirovisionLabels
            .filter((label: any) => label.type === 'VEHICLE')
            .map((label: any) => ({
                value: label.id,
                label: label.name,
            })),
    ];

    return (
        <fieldset>
            <Stack spacing={2}>
                <div>
                    <Typography variant='h2' component='legend'>
                        Experience Location
                    </Typography>
                    <Typography color='text.secondary' gutterBottom>
                        Tell guests where to find your experience
                    </Typography>
                </div>
                <div>
                    <InputLabel htmlFor='street-address__input'>
                        Address
                    </InputLabel>
                    <AddressInput />
                </div>
                <Divider>
                    <Typography variant='body2' color='text.secondary'>
                        or
                    </Typography>
                </Divider>
                <div>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <div>
                                <InputLabel htmlFor='street-address__input'>
                                    Street Address
                                </InputLabel>
                                <FormTextField
                                    id='street-address__input'
                                    name='streetAddress'
                                    control={control}
                                />
                            </div>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <div>
                                <InputLabel htmlFor='suburb__input'>
                                    Suburb
                                </InputLabel>
                                <FormTextField
                                    id='suburb__input'
                                    name='suburb'
                                    control={control}
                                />
                            </div>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <div>
                                <InputLabel htmlFor='state__input'>
                                    State
                                </InputLabel>
                                <FormAutocomplete
                                    id='state__input'
                                    name='state'
                                    control={control}
                                    options={[
                                        { value: 'NSW', label: 'NSW' },
                                        { value: 'QLD', label: 'QLD' },
                                        { value: 'VIC', label: 'VIC' },
                                        { value: 'SA', label: 'SA' },
                                        { value: 'WA', label: 'WA' },
                                        { value: 'NT', label: 'NT' },
                                        { value: 'TAS', label: 'TAS' },
                                        { value: 'ACT', label: 'ACT' },
                                    ]}
                                />
                            </div>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <div>
                                <InputLabel htmlFor='postcode__input'>
                                    Postcode
                                </InputLabel>
                                <FormTextField
                                    id='postcode__input'
                                    name='postcode'
                                    control={control}
                                    type='number'
                                />
                            </div>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <div>
                                <InputLabel htmlFor='country-code__input'>
                                    Country
                                </InputLabel>
                                <FormAutocomplete
                                    id='country-code__input'
                                    name='countryCode'
                                    control={control}
                                    options={[
                                        { value: 'AU', label: 'Australia' },
                                    ]}
                                />
                            </div>
                        </Grid>
                    </Grid>
                </div>
                <div>
                    <InputLabel htmlFor='phone__input'>
                        Property Contact Number
                    </InputLabel>
                    <FormTextField
                        id='phone__input'
                        name='phone'
                        control={control}
                        type='tel'
                    />
                </div>
                <div>
                    <InputLabel htmlFor='uses-nirovision__input'>
                        Req. Nirovision Access{' '}
                        <CustomTooltip
                            title={
                                <Typography>
                                    <List>
                                        <ListItem>
                                            Nirovision is a face recognition
                                            system that allows people access to
                                            premises.
                                        </ListItem>
                                        <ListItem>
                                            If you are not yet using Nirovision
                                            and wish to know more, visit
                                            https://www.nirovision.com/ and
                                            select No so guests do not receive
                                            correspondence.
                                        </ListItem>
                                    </List>
                                </Typography>
                            }
                            placement='right'
                        >
                            <HelpOutline sx={{ fontSize: 16 }} />
                        </CustomTooltip>
                    </InputLabel>
                    <FormAutocomplete
                        id='uses-nirovision__input'
                        name='usesNirovision'
                        control={control}
                        options={[
                            { value: true, label: 'Yes' },
                            { value: false, label: 'No' },
                        ]}
                    />
                </div>
                {usesNirovision && (
                    <>
                        <div>
                            <InputLabel htmlFor='nirovision-location__input'>
                                Nirovision Location
                            </InputLabel>
                            {nirovisionLocationOptions.length > 0 ? (
                                <FormAutocomplete
                                    id='nirovision-location__input'
                                    name='nirovisionLocationId'
                                    control={control}
                                    options={nirovisionLocationOptions}
                                />
                            ) : (
                                <Typography color='error'>
                                    Please link your Nirovision account to 7DAY
                                    in the Admin Centre.
                                </Typography>
                            )}
                        </div>
                        <div>
                            <Grid container spacing={2}>
                                <Grid item xs={12} md={6}>
                                    <InputLabel htmlFor='nirovision-label__input'>
                                        Nirovision Face Rec Label
                                    </InputLabel>
                                    {nirovisionLabelOptions.length > 0 ? (
                                        <FormAutocomplete
                                            id='nirovision-label__input'
                                            name='nirovisionLabelId'
                                            control={control}
                                            options={nirovisionLabelOptions}
                                        />
                                    ) : (
                                        <Typography color='error'>
                                            Please link your Nirovision account
                                            to 7DAY in the Admin Centre.
                                        </Typography>
                                    )}
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <InputLabel htmlFor='nirovision-vehicle-label__input'>
                                        Nirovision Vehicle Label
                                    </InputLabel>
                                    <FormAutocomplete
                                        id='nirovision-vehicle-label__input'
                                        name='nirovisionVehicleLabelId'
                                        control={control}
                                        options={nirovisionVehicleLabelOptions}
                                        onChange={(_, value) => {
                                            setValue(
                                                'nirovisionVehicleLabelId',
                                                value?.value ?? '',
                                                { shouldDirty: true },
                                            );
                                        }}
                                    />
                                </Grid>
                            </Grid>
                        </div>
                    </>
                )}
                <Box display='flex' alignItems='center' gap={1}>
                    <FormCheckbox
                        id='address-visible__input'
                        name='addressVisible'
                        control={control}
                    />
                    <InputLabel htmlFor='address-visible__input'>
                        Show address on experience page
                    </InputLabel>
                </Box>
                {experience.type === ExperienceType.Vehicle && (
                    <DropoffDetails
                        nirovisionLocationOptions={nirovisionLocationOptions}
                        nirovisionLabelOptions={nirovisionLabelOptions}
                        nirovisionVehicleLabelOptions={
                            nirovisionVehicleLabelOptions
                        }
                    />
                )}
            </Stack>
        </fieldset>
    );
};

const AddressInput = () => {
    const { clearErrors, setValue } = useFormContext<FormValues>();

    const handleAddress = (address: Address) => {
        clearErrors([
            'streetAddress',
            'suburb',
            'state',
            'postcode',
            'countryCode',
        ]);
        setValue('streetAddress', address.streetAddress, { shouldDirty: true });
        setValue('suburb', address.suburb, { shouldDirty: true });
        setValue('state', address.state as FormValues['state'], {
            shouldDirty: true,
        });
        setValue('postcode', address.postcode, { shouldDirty: true });
        setValue('countryCode', address.country as FormValues['countryCode'], {
            shouldDirty: true,
        });
    };

    return <AddressAutocomplete onChange={handleAddress} />;
};

const ExperienceLocation = () => {
    return <ExperienceLocationForm />;
};

export default ExperienceLocation;
